import { gql } from '@apollo/client'

import {
  BookingAddressesFragment,
  BookingDetailsFragment,
  BookingPackagesFragment,
  BookingTariffFragment,
  BookingAllocatedDriverFragment,
  BookingChargesFragment,
  BookingDriversFragment,
  DriverStatusFragment,
  ExchangeRateDetailsFragment,
  UserBookingDetailsFragment,
} from 'graphql/fragments'
import { FileDetailsFragment } from 'graphql/fragments'

export const GET_PAGED_BOOKINGS = gql`
  query getPagedBookings(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingSortInput!]
    $where: BookingFilterInput
  ) {
    bookings(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          pagedStatus
          statusLabel
          courierServicesStatusLabel
          collectionEstimatedTimeOfArrival
          deliveryEstimatedTimeOfArrival
          isOnHold
          isArchived
          isCancelledOrQuoteLost
          isPending
          isQuickQuote
          takenOn
          docs
          isUsingInternalApp
          consignmentFee
          contactId
          courierExchangeLoadId
          isBiddingEnabled
          driverBidsCount
          driverBids {
            id
          }
          contact {
            id
            name
          }
          customerId
          customer {
            id
            name
          }
          charges {
            id
            rate
            quantity
            isDeleted
            isAutomatic
          }
          customerReference
          addresses {
            id
            type
            sequenceOrder
            at
            by
            address {
              id
              name
              city
              postcode
            }
          }
          tariffId
          tariff {
            id
            name
            tariffCategory {
              id
              name
            }
            xeroCode {
              id
              code
              name
              isInternational
            }
          }
          attachments {
            id
          }
          drivers {
            charge
            driver {
              id
              name
            }
          }
          revenue
          packages {
            packageReference
          }
          hasTasks
          hasNotes
          takenBy
          salesInvoiceNumber
          invoices {
            id
            invoiceType
            invoiceNumber
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_PAGED_SUPPLIER_BOOKINGS = gql`
  query getPagedSupplierBookings(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingSortInput!]
    $where: BookingFilterInput
  ) {
    supplierBookings(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          pagedStatus
          statusLabel
          courierServicesStatusLabel
          collectionEstimatedTimeOfArrival
          deliveryEstimatedTimeOfArrival
          isOnHold
          isArchived
          contactId
          courierExchangeLoadId
          addresses {
            id
            type
            sequenceOrder
            at
            by
            address {
              id
              name
              city
              postcode
            }
          }

          contact {
            id
            name
          }
          customerId
          customer {
            id
            name
          }
          customerReference
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_USER_BOOKINGS = gql`
  query GerUserBookings(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingSortInput!]
    $where: BookingFilterInput
  ) {
    userBookings(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          pagedStatus
          statusLabel
          collectionEstimatedTimeOfArrival
          deliveryEstimatedTimeOfArrival
          isOnHold
          isArchived
          isCancelledOrQuoteLost
          isPending
          isQuickQuote
          takenOn
          docs
          isUsingInternalApp
          consignmentFee
          ourReference
          courierExchangeLoadId
          contactId
          contact {
            id
            name
          }
          customer {
            id
            name
            isReferenceRequired
          }
          charges {
            id
            rate
            quantity
            isDeleted
            isAutomatic
          }
          customerReference
          customerId
          customer {
            id
            name
          }
          addresses {
            id
            type
            sequenceOrder
            at
            by
            address {
              id
              name
              city
              postcode
            }
          }
          tariffId
          tariff {
            id
            name
          }
          attachments {
            id
          }
          distance
          packages {
            packageReference
          }
          hasNotes
          takenBy
          salesInvoiceNumber
          invoices {
            id
            invoiceType
            invoiceNumber
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_BOOKING_BY_TRACKING_HASH = gql`
  query GetBookingByTrackingHash($trackingHash: String!) {
    bookingByTrackingHash(hash: $trackingHash) {
      expiresOn
      isExpired
      referenceAddressId
      talkToAnExpertUrl
      booking {
        id
        ourReference
        distance
        consignmentFee
        statusLabel
        status
        allocatedDriverName
        driverLocationStatusLabel
        deliveryCompletedAt
        collectionCompletedAt
        deliveryPosition
        totalDeliveries
        currentDriverDelivery
        isDomesticDedicated
        deliveryGeometryLocation {
          latitude
          longitude
        }
        tariff {
          id
          rate
          minMiles
          minCharge
        }
        addresses {
          id
          at
          by
          sequenceOrder
          type
          totalPackagesWeight
          totalPackagesQuantity
          signedAt
          address {
            id
            addressLine1
            addressLine2
            postcode
            city
            countryId
            isVerified
            name
            googleMapsPlaceId
          }
          addressPackages {
            id
            package {
              id
              type
              weight
              length
              width
              height
            }
          }
          allocations {
            id
            package {
              id
              type
              weight
              length
              width
              height
            }
          }
        }
        bookingAttachments {
          fileId
          addressId
          addressType
          file {
            id
            name
            fileOrigin
            originalFileName
            absoluteUri
            category
          }
        }
        addressAttachments {
          fileId
          addressId
          addressType
          file {
            id
            name
            fileOrigin
            originalFileName
            absoluteUri
            category
          }
        }
      }
    }
  }
`

export const GET_BOOKING = gql`
  query GetBooking($bookingId: Long!) {
    booking(bookingId: $bookingId) {
      id
      ...BookingDetails
      ...BookingAddresses
      ...BookingDrivers
      ...BookingCharges
      ...BookingPackages
      ...BookingTariff
    }

    ${BookingDetailsFragment}
    ${BookingAddressesFragment}
    ${BookingDriversFragment}
    ${BookingChargesFragment}
    ${BookingPackagesFragment}
    ${BookingTariffFragment}
  }
`

export const GET_USER_BOOKING = gql`
    query GetUserBooking($bookingId: Long!) {
    userBooking(bookingId: $bookingId) {
      id
      ...UserBookingDetails
      ...BookingAddresses
      ...BookingCharges
      ...BookingPackages
      ...BookingTariff
    }

    ${UserBookingDetailsFragment}
    ${BookingAddressesFragment}
    ${BookingChargesFragment}
    ${BookingPackagesFragment}
    ${BookingTariffFragment}
  }
`

export const GET_SUPPLIER_BOOKING = gql`
  query GetSupplierBooking($bookingId: Long!) {
    supplierBooking(bookingId: $bookingId){
      id
      status
      statusLabel
      courierExchangeLoadId
      ourReference
      customerReference
      isOnHold
      isArchived
      isCancelledOrQuoteLost
      docketReference
      distance
      contactId
      contact {
        id
        name
        email
        telephoneNumber
      }

      overrideContactEmail
      overrideContactTelephoneNumber

      customerId
      customer {
        id
        name
      }

      packages {
        id
        name
        quantity
        type
        height
        width
        length
        weight
        packageReference
        type
        typeDescription

        collection {
          id
          address {
            id 
            name
          }
        }

        delivery {
          id
          address {
            id
            name
          }
        }
      }

      ...BookingAddresses
    }
    ${BookingAddressesFragment}
  }
`

export const GET_BOOKING_ADDRESSES = gql`
    query GetBookingAddresses($bookingId: Long!){
        booking(bookingId: $bookingId) {
            id
            ...BookingAddresses
        }
        ${BookingAddressesFragment}
    }
`

export const GET_BOOKING_ADDRESSES_DATA = gql`
  query GetBookingAddressesData($bookingId: Long!, $where: BookingAddressFilterInput) {
    bookingAddresses(bookingId: $bookingId, where: $where) {
      id
      address {
        id
        name
        postcode
      }
    }
  }
`

export const GET_USER_BOOKING_ADDRESSES = gql`
  query GetUserBookingAddresses($bookingId: Long!) {
    userBooking(bookingId: $bookingId){
      id
            ...BookingAddresses
    }
    ${BookingAddressesFragment}
  }
`

export const GET_BOOKING_ADDRESS_SIGNATURE = gql`
    query GetBookingAddressSignature($bookingAddressId: Long!){
      bookingAddressSignature(id: $bookingAddressId) {
        id
        ...FileDetails
      }
      ${FileDetailsFragment}
    }
`

export const GET_BOOKING_ADDRESS_ATTACHMENTS = gql`
  query GetBookingAddressAttachments($id: Long!){
    bookingAddress(id: $id) {
      id
      type
      bookingId
      attachments {
        id
        ...FileDetails
      }
      paperworkAttachments {
        id
        ...FileDetails
      }
      ${FileDetailsFragment}
    }
  }
`

export const GET_BOOKING_ADDRESSES_ATTACHMENTS_DESCRIPTION = gql`
  query GetBookingAddressesAttachmentsDescription(
    $bookingId: Long!
    $where: BookingAddressFilterInput
  ) {
    bookingAddresses(bookingId: $bookingId, where: $where) {
      id
      type
      sequenceOrder
      address {
        id
        name
      }
    }
  }
`

export const GET_BOOKING_ALLOCATED_DRIVERS = gql`
  query GetBookingAllocatedDrivers(
    $bookingId: Long!
    $order: [BookingDriverSortInput!]
    $where: BookingDriverFilterInput
  ) {
    bookingAllocatedDrivers(bookingId: $bookingId, order: $order, where: $where) {
      id
      charge
      extraPayment
      booking {
        id
        isBiddingEnabled
      }
      driver {
        id
        name
      }
      vehicle {
        id
        vehicleType {
          id
          name
        }
      }
    }
  }
`

export const GET_BOOKING_ALLOCATED_DRIVERS_RATINGS = gql`
  query GetBookingAllocatedDriversRatings(
    $bookingId: Long!
    $order: [BookingDriverSortInput!]
    $where: BookingDriverFilterInput
  ) {
    bookingAllocatedDrivers(bookingId: $bookingId, order: $order, where: $where) {
      id
      rating
    }
  }
`

export const GET_PAGED_BOOKING_ALLOCATED_DRIVERS = gql`
  query GetPagedBookingAllocatedDrivers(
    $bookingId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingDriverSortInput!]
    $where: BookingDriverFilterInput
  ) {
    pagedBookingAllocatedDrivers(
      bookingId: $bookingId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          charge
          extraPayment
          createdAt
          xeroPurchaseCode
          allocationStatus
          createdAt
          totalCharge
          driverNotes
          reference
          rating
          bookingExchangeRates {
            id
            stage
            exchangeRate {
              id
              rate

              sourceCurrency {
                id
                code
              }

              targetCurrency {
                id
                code
              }
            }
          }
          driver {
            id
            name
            callSign
            driverType
            xeroCode {
              id
              name
            }
            primaryDriver {
              id
              name
            }
            primaryDriverName
            identityUserName
            isUsingInternalApp
          }
          vehicle {
            id
            vehicleType {
              id
              name
            }
          }
          currency {
            id
            code
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_PAGED_SUPPLIERS_BOOKING_ALLOCATED_DRIVERS = gql`
  query GetPagedSuppliersBookingAllocatedDrivers(
    $bookingId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingDriverSortInput!]
    $where: BookingDriverFilterInput
  ) {
    pagedSupplierBookingAllocatedDrivers(
      bookingId: $bookingId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          charge
          extraPayment
          createdAt
          allocationStatus
          bookingId
          createdAt
          totalCharge
          driverNotes
          reference
          isShownInstruction
          instruction
          driverId
          driver {
            id
            name
            primaryDriver {
              id
              name
            }
            primaryDriverName
            identityUserName
            isUsingInternalApp
            driverType
          }
          vehicle {
            id
            vehicleType {
              id
              name
            }
          }
          currency {
            id
            code
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_BOOKING_ALLOCATED_DRIVER = gql`
  query GetBookingAllocatedDriver($bookingDriverId: Long!) {
    bookingAllocatedDriver(bookingDriverId: $bookingDriverId) {
      id
      ...BookingAllocatedDriver
    }
    ${BookingAllocatedDriverFragment}
  }
`

export const GET_INVOICE_BOOKINGS = gql`
  query getInvoiceBookings(
    $invoiceNumber: Long!
    $invoiceType: InvoiceType!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingSortInput!]
    $where: BookingFilterInput
  ) {
    invoiceBookings(
      invoiceNumber: $invoiceNumber
      invoiceType: $invoiceType
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          customerReference
          status
          createdAt
        }
      }
      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_PAGED_BOOKING_INVOICES_BY_CUSTOMER = gql`
  query GetBookingInvoice(
    $customerId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingInvoiceSortInput!]
    $where: BookingInvoiceFilterInput
  ) {
    bookingInvoice(
      customerId: $customerId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          invoiceNumber
          invoiceDate
          reference
          bookingId
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_BOOKING_LOAD_ID = gql`
  query GetBookingLoadId($bookingId: Long!) {
    booking(bookingId: $bookingId) {
      id
      courierExchangeLoadId
    }
  }
`

export const GET_BOOKING_LOGS = gql`
  query GetBookingLogs(
    $bookingId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingLogSortInput!]
  ) {
    bookingLogs(
      bookingId: $bookingId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
    ) {
      edges {
        node {
          id
          createdAt
          createdBy
          type
          description
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_LOG_RECORD = gql`
  query GetBookingLog($bookingLogId: Long!) {
    bookingLog(bookingLogId: $bookingLogId) {
      id
      type
      description
      details
    }
  }
`

export const GET_BOOKING_AUDIT = gql`
  query GetBookingAudit(
    $bookingId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [AuditRecordSortInput!]
  ) {
    bookingAudit(
      bookingId: $bookingId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
    ) {
      edges {
        node {
          id
          createdAt
          createdBy
          name
          primaryKey
          action
          changes
          columnValues
          transactionId
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_AUDIT_RECORD = gql`
  query GetAuditRecord($auditRecordId: Long!) {
    auditRecord(auditRecordId: $auditRecordId) {
      id
      name
      action
      columnValues
      success
      errorMessage
    }
  }
`

export const GET_QUOTE = gql`
  query GetQuote($quoteId: Long!) {
    booking(bookingId: $quoteId) {
      id
      docketReference
      takenBy
      takenOn
      customer {
        id
        name
      }
      drivers {
        id
      }

      ...BookingDetails
      ...BookingAddresses
      ...BookingCharges
      ...BookingTariff
    }
    ${BookingDetailsFragment}
    ${BookingAddressesFragment}
    ${BookingChargesFragment}
    ${BookingTariffFragment}
  }
`

export const GET_USER_QUOTE = gql`
  query GetUserQuote($quoteId: Long!) {
    userBooking(bookingId: $quoteId) {
      id
      docketReference
      takenBy
      takenOn
      customer {
        id
        name
      }
      drivers {
        id
      }

      ...UserBookingDetails
      ...BookingAddresses
      ...BookingCharges
      ...BookingTariff
    }
    ${UserBookingDetailsFragment}
    ${BookingAddressesFragment}
    ${BookingChargesFragment}
    ${BookingTariffFragment}
  }
`

export const GET_PAGED_CUSTOMER_INVOICE = gql`
  query getPagedCustomerInvoice(
    $customerId: Long!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [CustomerInvoiceDtoSortInput!]
    $where: CustomerInvoiceDtoFilterInput
  ) {
    pagedCustomerInvoices(
      customerId: $customerId
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          bookings {
            id
            status
            customerReference
            createdAt
            collectionEstimatedTimeOfArrival
          }

          bookingStatuses
          ourReferences
          customerReferences
          bookingDate
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_PAGED_INVOICE_LINES = gql`
  query GetPagedInvoiceLines(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingInvoiceContentSortInput!]
    $where: BookingInvoiceContentFilterInput
  ) {
    pagedInvoiceLines(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          unitAmount
          accountCode
          description
          bookingInvoice {
            invoiceNumber
            reference
            invoiceDate
            dueDate
            bookingId
            booking {
              id
              ourReference
            }
            customer {
              name
              postcode
              customerReferenceLabel
            }
            driverInvoiceNumber
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_BOOKING_EXCHANGE_RATES = gql`
  query getBookingExchangeRates($bookingId: Long!) {
    bookingExchangeRates(bookingId: $bookingId) {
      id
      stage
      bookingId
      exchangeRateId
      exchangeRate {
        ...ExchangeRateDetails
      }
      createdAt
    }
  }
  ${ExchangeRateDetailsFragment}
`

export const GET_BOOKING_TOTAL_DRIVER_CHARGE = gql`
  query getBookingDriverTotalCharge($bookingId: Long!) {
    booking(bookingId: $bookingId) {
      id
      totalDriverCharge
    }
  }
`

export const GET_PAGED_CANCELLATION_OR_LOST_REASONS = gql`
  query GetPagedCancellationOrLostReasons(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [CancellationOrLostReasonSortInput!]
    $where: CancellationOrLostReasonFilterInput
  ) {
    pagedCancellationOrLostReasons(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          name
          code
          createdAt
          createdBy
          isActive
          sendCustomerEmailNotification
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_CANCELLATION_OR_LOST_REASONS = gql`
  query GetCancellationOrLostReasons {
    cancellationOrLostReasons {
      id
      name
      code
      isActive
      sendCustomerEmailNotification
    }
  }
`

export const GET_CANCELLATION_OR_LOST_REASON = gql`
  query GetCancellationOrLostReason($id: Long!) {
    cancellationOrLostReason(id: $id) {
      id
      name
      code
      isActive
      sendCustomerEmailNotification
    }
  }
`

export const GET_BOOKING_USERS_QUERY = gql`
  query getBookingUsers($where: BookingUserDtoFilterInput) {
    bookingUsers(where: $where) {
      id
      email
      userName
      lastName
      firstName
    }
  }
`

export const GET_BOOKING_CREATORS_QUERY = gql`
  query getBookingCreators($where: BookingUserDtoFilterInput) {
    bookingCreators(where: $where) {
      id
      email
      userName
      lastName
      firstName
    }
  }
`

export const GET_POTENTIAL_BOOKING_FILES = gql`
  query GetPotentialBookingFiles(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [PotentialBookingFileSortInput!]
    $where: PotentialBookingFileFilterInput
  ) {
    potentialBookingFiles(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          file {
            id
            originalFileName
          }
          uploadedByCustomer {
            id
            name
          }
          isProcessed
          createdAt
          createdBy
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_POTENTIAL_BOOKING_FILE = gql`
  query GetPotentialBookingFile($id: Long!) {
    potentialBookingFile(id: $id) {
      id
      file {
        id
        originalFileName
      }
      uploadedByCustomer {
        id
        name
      }
      isProcessed
      createdAt
      createdBy
      extractedData
    }
  }
`

export const GET_POTENTIAL_BOOKING_MAPPINGS = gql`
  query GetPotentialBookingMappings {
    potentialBookingMappings {
      id
      name
    }
  }
`

export const GET_POTENTIAL_BOOKINGS = gql`
  query GetPotentialBookings(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [PotentialBookingSortInput!]
    $where: PotentialBookingFilterInput
  ) {
    potentialBookings(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          potentialBookingFile {
            id
            file {
              id
              name
            }
          }
          potentialBookingMapping {
            id
            name
          }
          bookingIdentifierKey
          bookingIdentifierValue
          customerIdentifierKey
          customerIdentifierValue
          isProcessed
          createdAt
          createdBy
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_POTENTIAL_BOOKING = gql`
  query GetPotentialBooking($id: Long!) {
    potentialBooking(id: $id) {
      id
      bookingIdentifierKey
      bookingIdentifierValue
      customerIdentifierKey
      customerIdentifierValue
      potentialBookingFile {
        id
        extractedData
      }
      potentialBookingMapping {
        id
        type
      }
    }
  }
`

export const GET_PAGED_BOOKINGS_TRACKING_DASHBOARD_DATA = gql`
  query GetPagedBookingsTrackingDashboard(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingTrackingDashboardDtoSortInput!]
    $where: BookingTrackingDashboardDtoFilterInput
  ) {
    bookingTrackingDashboardData(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          statusLabel
          customerName
          customerReference
          driverStatus {
            ...DriverStatusFields
          }
        }
      }
      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
  ${DriverStatusFragment}
`

export const GET_PAGED_DRIVER_BOOKINGS_TRACKING_DASHBOARD_DATA = gql`
  query GetPagedDriverBookingsTrackingDashboard(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingTrackingDashboardDtoSortInput!]
    $where: BookingTrackingDashboardDtoFilterInput
  ) {
    driverBookingTrackingDashboardData(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          statusLabel
          customerName
          customerReference
          driverStatus {
            ...DriverStatusFields
          }
        }
      }
      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
  ${DriverStatusFragment}
`

export const GET_PAGED_USER_BOOKINGS_TRACKING_DASHBOARD_DATA = gql`
  query GetPagedUserBookingsTrackingDashboard(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingTrackingDashboardDtoSortInput!]
    $where: BookingTrackingDashboardDtoFilterInput
  ) {
    userBookingTrackingDashboardData(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          statusLabel
          customerName
          customerReference
          driverStatus {
            ...DriverStatusFields
          }
        }
      }
      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
  ${DriverStatusFragment}
`

export const GET_DRIVER_BOOKINGS_QUERY = gql`
  query GetDriverBookings(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [BookingSortInput!]
    $where: BookingFilterInput
  ) {
    driverBookings(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          status
          pagedStatus
          statusLabel
          courierServicesStatusLabel
          collectionEstimatedTimeOfArrival
          deliveryEstimatedTimeOfArrival
          isOnHold
          isArchived
          isCancelledOrQuoteLost
          isPending
          isQuickQuote
          takenOn
          docs
          isUsingInternalApp
          consignmentFee
          ourReference
          contactId
          courierExchangeLoadId
          contact {
            id
            name
          }
          customer {
            id
            name
          }
          charges {
            id
            rate
            quantity
            isDeleted
            isAutomatic
          }
          customerReference
          customerId
          customer {
            id
            name
          }
          addresses {
            id
            type
            sequenceOrder
            at
            by
            address {
              id
              name
              city
              postcode
            }
          }
          tariffId
          tariff {
            id
            name
            tariffCategory {
              id
              name
            }
            xeroCode {
              id
              code
              name
              isInternational
            }
          }
          attachments {
            id
          }
          drivers {
            charge
            driver {
              id
              name
            }
          }
          revenue
          packages {
            packageReference
          }
          hasTasks
          hasNotes
          takenBy
          salesInvoiceNumber
          invoices {
            id
            invoiceType
            invoiceNumber
          }
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_PAGED_AVAILABLE_LOADS = gql`
  query getPagedAvailableLoads(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $order: [AvailableLoadDtoSortInput!]
    $where: AvailableLoadDtoFilterInput
  ) {
    availableLoads(
      after: $after
      before: $before
      first: $first
      last: $last
      order: $order
      where: $where
    ) {
      edges {
        node {
          id
          ourReference
          distance
          tariffCategoryName
          collectionAddressCity
          collectionAddressOutcode
          collectioReadyAt
          collectBy
          deliverBy
          deliveryAddressCity
          deliveryAddressOutcode
          deliverFrom
          packageType
          packageQuantity
          packageWeight
          packageLength
          packageWidth
          packageHeight
          hasDriverPlacedBid
          isDriverBidActive
          notes
        }
      }

      totalCount
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
    }
  }
`

export const GET_TOP_CUSTOMER_BOOKINGS = gql`
  query GetTopCustomerBookings($customerId: Long!) {
    topCustomerBookings(customerId: $customerId) {
      id
      postcode
      bookingId
      tariffName

      firstCollectionAddress {
        id
        type
        address {
          id
          name
          city
          postcode
        }
      }

      lastDeliveryAddress {
        id
        type
        address {
          id
          name
          city
          postcode
        }
      }
    }
  }
`

export const GET_USER_TOP_BOOKINGS = gql`
  query GetUserTopBookings {
    userTopBookings {
      id
      postcode
      bookingId
      tariffName

      firstCollectionAddress {
        id
        type
        address {
          id
          name
          city
          postcode
        }
      }

      lastDeliveryAddress {
        id
        type
        address {
          id
          name
          city
          postcode
        }
      }
    }
  }
`

export const GET_ETA_TO_BOOKING_BY_TRACKING_HASH_QUERY = gql`
  query GetEtaToBookingByTrackingHash($hash: String!) {
    etaToBookingByTrackingHash(hash: $hash) {
      nextAddressETA
      etaWindowMinutes
      etaSource
      etaLastUpdatedAt
      stopName
      stopAddress
    }
  }
`

export const GET_BOOKING_ALL_ADDRESSES_ETA = gql`
  query GetBookingAllAddressesEta($bookingId: Long!) {
    bookingAllAddressesEta(bookingId: $bookingId) {
      bookingAddressId
      nextAddressETA
      etaWindowMinutes
      etaSource
      etaLastUpdatedAt
      stopName
      stopAddress
    }
  }
`

export const GET_ALL_ADDRESSES_ETA_BY_TRACKING_HASH = gql`
  query GetAllAddressesEtaByTrackingHash($hash: String!) {
    allAddressesEtaByTrackingHash(hash: $hash) {
      bookingAddressId
      nextAddressETA
      etaWindowMinutes
      etaSource
      etaLastUpdatedAt
      stopName
      stopAddress
    }
  }
`

export const GET_BOOKING_NEXT_ADDRESS_ETA = gql`
  query GetBookingNextAddressEta($bookingId: Long!) {
    bookingNextAddressEta(bookingId: $bookingId) {
      nextAddressETA
      etaWindowMinutes
      etaSource
      etaLastUpdatedAt
      stopName
      stopAddress
    }
  }
`
